/**
 * Atajos de iOS: una clave por propósito (Tickets/Pluxee y Tricount) para que los Atajos escriban en tu libro sin
 * abrir la app. La clave se ve una sola vez al crearla; el servidor solo guarda su huella.
 */
import { api } from './adapters/api-client.js';
import { $, esc, dayLabel } from './ui/format.js';
import { toast, stickyBar } from './ui/feedback.js';
import { frame, setView, type ViewState } from './views.js';

type Purpose = 'tickets' | 'tricount';
const INFO: Record<Purpose, { title: string; what: string; body: (key: string) => object }> = {
  tickets: {
    title: 'Clave de Tickets (Pluxee)', what: 'Deja apuntar pagos y cargas de la tarjeta de comida en tu cuenta de Tickets.',
    body: key => ({ action: 'tickets.insert', key, client: 'pf-tickets/1', date: '2026-10-09', concepto: 'Menú del día', importe: '11,50', isCarga: false, isShared: false, note: '' }),
  },
  tricount: {
    title: 'Clave de Tricount', what: 'Deja añadir gastos a tu Tricount (el que conectaste en Más → Tricount) y consultar quién debe a quién.',
    body: key => ({ action: 'tricount.add', key, client: 'pf-efectivo/1', Fecha: '2026-10-09', Concepto: 'Cena', Cantidad: '40,00', Origen: 'Efectivo' }),
  },
};
/** Atajos publicados (los genera apps/shortcuts/build.mjs, sin claves dentro: cada uno las pide al instalarlo). */
const SHORTCUTS = [
  { file: 'Pago con Tickets', needs: ['tickets', 'tricount'] as Purpose[],
    what: 'Al pagar con la tarjeta de Tickets lo apunta en tu cuenta de Tickets, te pregunta si lo añades a Tricount (todo, otro importe o nada) y termina con el saldo de la tarjeta y quién debe a quién. Si lo añades, en tu libro queda como compartido (con otro importe, enlaza el resto como reembolsos).' },
  { file: 'Gasto en efectivo', needs: ['tricount'] as Purpose[],
    what: 'Pregunta importe y concepto y lo añade a Tricount como pagado por ti en efectivo. También sirve si el otro falla.' },
];

export function viewShortcuts(s: ViewState) {
  frame(s, { title: 'Atajos de iOS', tab: 'mas', back: true });
  stickyBar('');
  if (s.demo) return setView('<section class="card"><div class="sub">En la demostración no hay Atajos.</div></section>');
  const url = `${location.origin}/api/ingest`;
  const env = location.hostname.includes('-dev') ? 'dev' : 'prod';
  const fileUrl = (name: string) => `/atajos/${env}/${encodeURIComponent(`${name}${env === 'dev' ? ' (DEV)' : ''}`)}.shortcut`;
  // Las claves recién creadas se quedan a la vista mientras sigas en esta pantalla: el Atajo de Tickets pide dos
  const fresh: Partial<Record<Purpose, string>> = {};
  const published = new Set<string>();
  const draw = (list: Array<{ purpose: Purpose; createdAt: string }>) => {
    const has = (p: Purpose) => list.some(x => x.purpose === p);
    setView(`
      <section class="card"><h2>Cómo se instala</h2>
        <ol class="sub steps">
          <li><b>Crea las claves</b> que necesita el Atajo (abajo) y <b>no salgas de esta pantalla</b>: cada clave solo se ve ahora.</li>
          <li>Desde el iPhone, toca <b>Instalar</b> y luego <b>Añadir atajo</b>.</li>
          <li>El iPhone te pedirá cada clave: vuelve aquí, toca <b>Copiar</b> y pégala.</li>
          <li>Para el de Tickets, en tu <b>automatización</b> de la tarjeta (Atajos → Automatización), haz que ejecute «Pago con Tickets» con la misma entrada: una lista con el comercio y el importe.</li>
        </ol>
        <div class="sub">Lo que pagas por Tricount queda a tu nombre (tu «Tú eres» de Más → Tricount): el mismo Atajo sirve para cada persona con sus propias claves.</div>
      </section>
      ${SHORTCUTS.map(x => `<section class="card"><h2>${esc(x.file)}</h2><div class="sub">${esc(x.what)}</div>
        <div class="sub" style="margin-top:6px">Necesita: ${x.needs.map(p => `${esc(INFO[p].title.replace('Clave de ', ''))} ${has(p) ? '<span class="pos">✓</span>' : '<span class="neg">(falta la clave)</span>'}`).join(' · ')}</div>
        <div class="actions">${published.has(x.file) ? `<a class="btn small" href="${fileUrl(x.file)}" download>Instalar</a>` : '<span class="sub">Aún no está publicado.</span>'}</div>
      </section>`).join('')}
      ${(Object.keys(INFO) as Purpose[]).map(p => {
        const t = list.find(x => x.purpose === p), i = INFO[p];
        const shown = fresh[p] ?? '';
        return `<section class="card"><h2>${esc(i.title)}</h2><div class="sub">${esc(i.what)}</div>
          <div class="sub" style="margin-top:6px">${t ? `Creada el ${esc(dayLabel(t.createdAt.slice(0, 10)))}` : 'Sin clave'}</div>
          ${shown ? `<div class="card attention" style="margin:10px 0"><div class="sub"><b>Tu clave nueva</b> (pégala en el Atajo: al salir de esta pantalla no se vuelve a mostrar)</div>
              <div class="keybox">${esc(shown)}</div><div class="actions"><button class="btn small" data-copy="${p}">Copiar</button></div></div>` : ''}
          <div class="actions"><button class="btn small${t ? ' secondary' : ''}" data-create="${p}">${t ? 'Crear otra clave' : 'Crear clave'}</button>${t ? ` <button class="btn small secondary" data-remove="${p}">Quitar clave</button>` : ''}</div>
        </section>`;
      }).join('')}
      <section class="card"><details><summary class="sub">Para desarrolladores: cómo llamar al servicio</summary>
        <div class="sub">Los Atajos llaman a <code>${esc(url)}</code> con <b>POST</b> y un cuerpo <b>JSON</b>. Ejemplos:</div>
        ${(Object.keys(INFO) as Purpose[]).map(p => `<pre class="json">${esc(JSON.stringify(INFO[p].body('‹clave›'), null, 2))}</pre>`).join('')}
        <div class="sub">La respuesta siempre trae <code>ok</code> y <code>message</code> (un texto listo para enseñar, también en los errores). <code>requestId</code> es opcional: sin él, dos envíos iguales en el mismo minuto cuentan como uno. <code>tricount.balance</code> solo consulta quién debe a quién. Si una clave se filtra o pierdes el iPhone, crea otra: la anterior deja de valer.</div>
      </details></section>`);
    $('#view').onclick = async e => {
      const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button');
      if (!b) return;
      try {
        if (b.dataset.copy) { await navigator.clipboard.writeText(fresh[b.dataset.copy as Purpose] ?? ''); toast('✓ Clave copiada'); return; }
        if (b.dataset.create) {
          const p = b.dataset.create as Purpose;
          if (list.some(x => x.purpose === p) && !confirm('La clave actual dejará de funcionar en tus Atajos. ¿Crear otra?')) return;
          fresh[p] = (await api.ingestTokens.create(p)).key;
          draw(await api.ingestTokens.list());
        }
        if (b.dataset.remove) {
          if (!confirm('Los Atajos que usen esta clave dejarán de funcionar. ¿Quitarla?')) return;
          await api.ingestTokens.remove(b.dataset.remove as Purpose);
          delete fresh[b.dataset.remove as Purpose];
          toast('Clave quitada');
          draw(await api.ingestTokens.list());
        }
      } catch (err) { toast((err as Error).message, 'error'); }
    };
  };
  setView('<div class="loading">Consultando…</div>');
  // Qué Atajos están publicados en este entorno: la lista la escribe el generador al firmarlos (sin lista, ninguno)
  const check = fetch(`/atajos/${env}/index.json`, { cache: 'no-cache' }).then(r => r.json())
    .then((names: unknown) => { if (Array.isArray(names)) names.forEach(n => published.add(String(n).replace(/ \(DEV\)$/, ''))); }).catch(() => {});
  Promise.all([api.ingestTokens.list(), check]).then(([l]) => draw(l)).catch(err => setView(`<div class="error">${esc((err as Error).message)}</div>`));
}
